import { Component } from '@theme/component';

/**
 * <frelan-form> — the generic website form used by `sections/frelan-form.liquid`.
 *
 * Fields are theme blocks (form-field, form-select, form-textarea, form-checkboxes,
 * form-consent) that mark their control with `data-form-field`; this component discovers
 * them at submit time, validates the required ones, then POSTs the whole form as JSON to
 * the Cloudflare Worker named in `data-endpoint-url`, tagged with `data-form-key` so the
 * Worker knows which route (HubSpot form, notification address) to use.
 *
 * Generalised from `trade-account-form.js` (2026-09-14).
 *
 * @typedef {Object} FrelanFormRefs
 * @property {HTMLFormElement} form
 * @property {HTMLDivElement} formError
 * @property {HTMLSpanElement} formErrorText
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLDivElement} [success]
 * @property {HTMLDivElement} [liveRegion]
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * @extends {Component<FrelanFormRefs>}
 */
class FrelanForm extends Component {
  requiredRefs = ['form', 'formError', 'formErrorText', 'submitButton'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.form.addEventListener('submit', this.#handleSubmit);
    this.#prefillCheckboxesFromUrl();
  }

  /**
   * Tick checkboxes named in the URL, e.g. /pages/request-brochures?brochures=burlington
   * (the brochures page links here per card). Checkboxes only, and only exact
   * name + value matches, so a link can pre-select an option but never inject text.
   */
  #prefillCheckboxesFromUrl() {
    const params = new URLSearchParams(window.location.search);
    for (const [name, value] of params) {
      for (const box of this.refs.form.querySelectorAll('input[type="checkbox"]')) {
        if (box instanceof HTMLInputElement && box.name === name && box.value === value) {
          box.checked = true;
        }
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form.removeEventListener('submit', this.#handleSubmit);
  }

  /** @param {string} key @param {string} fallback */
  #msg(key, fallback) {
    return this.dataset[key] || fallback;
  }

  /** @returns {HTMLElement[]} */
  #fields() {
    return Array.from(this.refs.form.querySelectorAll('[data-form-field]'));
  }

  /** @param {HTMLElement} field */
  #errorContainerFor(field) {
    const id = field.id;
    if (!id) return null;
    return this.refs.form.querySelector(`[data-error-for="${id}"]`);
  }

  /** @param {HTMLElement} field @param {string} message */
  #setFieldError(field, message) {
    field.setAttribute('aria-invalid', 'true');
    const container = this.#errorContainerFor(field);
    if (container instanceof HTMLElement) {
      container.classList.remove('hidden');
      const span = container.querySelector('span');
      if (span) span.textContent = message;
      if (container.id) field.setAttribute('aria-describedby', container.id);
    }
  }

  #clearFieldErrors() {
    for (const field of this.#fields()) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
      const container = this.#errorContainerFor(field);
      if (container instanceof HTMLElement) {
        container.classList.add('hidden');
        const span = container.querySelector('span');
        if (span) span.textContent = '';
      }
    }
  }

  #clearFormBanners() {
    this.refs.formError.classList.add('hidden');
    this.refs.formErrorText.textContent = '';
  }

  /**
   * Client-side check mirroring the Worker's validation — faster feedback, no round trip.
   * @returns {boolean}
   */
  #validate() {
    /** @type {HTMLElement | null} */
    let firstInvalid = null;
    const requiredMsg = this.#msg('msgRequired', 'This field is required.');
    const emailMsg = this.#msg('msgInvalidEmail', 'Please enter a valid email address.');
    const chooseMsg = this.#msg('msgChooseOne', 'Please choose at least one option.');

    for (const field of this.#fields()) {
      const type = field.dataset.fieldType;
      let problem = '';

      if (type === 'checkboxes') {
        const required = field.dataset.required === 'true';
        const anyChecked = field.querySelector('input[type="checkbox"]:checked');
        if (required && !anyChecked) problem = chooseMsg;
      } else if (type === 'consent') {
        if (field instanceof HTMLInputElement && field.required && !field.checked) problem = requiredMsg;
      } else if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement) {
        const value = String(field.value).trim();
        if (field.required && !value) {
          problem = requiredMsg;
        } else if (value && type === 'email' && !EMAIL_RE.test(value)) {
          problem = emailMsg;
        }
      }

      if (problem) {
        this.#setFieldError(field, problem);
        firstInvalid ??= field;
      }
    }

    if (firstInvalid) {
      const focusTarget = firstInvalid.matches('fieldset')
        ? firstInvalid.querySelector('input')
        : firstInvalid;
      if (focusTarget instanceof HTMLElement) focusTarget.focus();
      firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return false;
    }
    return true;
  }

  /** @param {string} message */
  #showFormError(message) {
    this.refs.formError.classList.remove('hidden');
    this.refs.formErrorText.textContent = message;
    this.refs.formError.focus();
    if (this.refs.liveRegion) this.refs.liveRegion.textContent = message;
  }

  #showSuccess() {
    this.refs.form.hidden = true;
    if (this.refs.success) {
      this.refs.success.hidden = false;
      this.refs.success.focus();
      this.refs.success.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = this.#msg('msgSuccess', 'Thank you — your message has been sent.');
    }
  }

  /**
   * FormData → plain object; repeated keys (checkbox groups) become arrays.
   * @returns {Record<string, string | string[]>}
   */
  #payload() {
    /** @type {Record<string, string | string[]>} */
    const payload = {};
    const formData = new FormData(this.refs.form);
    for (const [key, raw] of formData.entries()) {
      const value = typeof raw === 'string' ? raw : '';
      if (key in payload) {
        const existing = payload[key];
        payload[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
      } else {
        payload[key] = value;
      }
    }
    // Consent checkboxes report explicitly, ticked or not.
    for (const field of this.#fields()) {
      if (field.dataset.fieldType === 'consent' && field instanceof HTMLInputElement && !(field.name in payload)) {
        payload[field.name] = 'false';
      }
    }
    payload.form_key = this.dataset.formKey || '';
    payload.page_url = window.location.href;
    payload.turnstile_token = String(formData.get('cf-turnstile-response') ?? '');
    delete payload['cf-turnstile-response'];
    return payload;
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();
    this.#clearFormBanners();
    this.#clearFieldErrors();
    if (!this.#validate()) return;

    const endpointUrl = this.dataset.endpointUrl;
    if (!endpointUrl) {
      this.#showFormError(this.#msg('msgConfigError', 'This form is not configured yet.'));
      return;
    }

    const payload = this.#payload();
    const { submitButton } = this.refs;
    const originalLabel = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = this.#msg('msgSubmitting', 'Sending…');

    try {
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({ ok: false }));
      if (response.ok && result.ok) {
        this.refs.form.reset();
        this.#showSuccess();
      } else {
        this.#showFormError(result.error || this.#msg('msgError', 'Something went wrong. Please try again.'));
      }
    } catch {
      this.#showFormError(this.#msg('msgNetworkError', 'Could not reach the server. Please check your connection and try again.'));
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = originalLabel;
    }
  };
}

if (!customElements.get('frelan-form')) {
  customElements.define('frelan-form', FrelanForm);
}
